import React, { useEffect, useState } from "react";
import { RiResetLeftLine } from "react-icons/ri";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { CircularButton } from "./CircularButton";

interface BarSphereProps {
  currentBar: number;
  totalBars: number;
  onReset?: () => void;
  onStartBarChange?: (bar: number) => void;
  onNextBar?: () => void;
  onPreviousBar?: () => void;
  loop?: boolean;
}

export const BarSphere: React.FC<BarSphereProps> = ({
  currentBar,
  totalBars,
  onReset,
  onStartBarChange,
  onNextBar,
  onPreviousBar,
  loop = false,
}) => {
  const [isEditingStartBar, setIsEditingStartBar] = useState(false);
  const [startBarInputValue, setStartBarInputValue] = useState(
    currentBar.toString()
  );

  // Keep input in sync with prop when not editing
  useEffect(() => {
    if (!isEditingStartBar) {
      setStartBarInputValue(currentBar.toString());
    }
  }, [currentBar, isEditingStartBar]);

  const handleStartBarClick = () => {
    setIsEditingStartBar(true);
    setStartBarInputValue(currentBar.toString());
  };

  const handleStartBarInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleStartBarInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    // Only allow numbers, up to 3 digits
    if (/^\d{0,3}$/.test(value)) {
      setStartBarInputValue(value);
    }
  };

  const validateAndSetStartBar = (value: string) => {
    let numValue = parseInt(value, 10);

    if (isNaN(numValue) || value === "") {
      numValue = currentBar; // keep current if invalid
    } else {
      // clamp between 1 and totalBars
      if (numValue < 1) numValue = 1;
      if (numValue > totalBars) numValue = totalBars;
    }

    onStartBarChange?.(numValue);
    setStartBarInputValue(numValue.toString());
    setIsEditingStartBar(false);
  };

  const handleStartBarInputBlur = () => {
    validateAndSetStartBar(startBarInputValue);
  };

  const handleStartBarInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      validateAndSetStartBar(startBarInputValue);
    } else if (e.key === "Escape") {
      setStartBarInputValue(currentBar.toString());
      setIsEditingStartBar(false);
    }
  };

  const handlePreviousBar = () => {
    // Use the passed callback if available, otherwise fall back to onStartBarChange
    if (onPreviousBar) {
      onPreviousBar();
    } else if (currentBar > 1) {
      onStartBarChange?.(currentBar - 1);
    } else if (loop && currentBar === 1) {
      // Wrap to last bar when looping
      onStartBarChange?.(totalBars);
    }
  };

  const handleNextBar = () => {
    // Use the passed callback if available, otherwise fall back to onStartBarChange
    if (onNextBar) {
      onNextBar();
    } else if (currentBar < totalBars) {
      onStartBarChange?.(currentBar + 1);
    } else if (loop && currentBar === totalBars) {
      // Wrap to first bar when looping
      onStartBarChange?.(1);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "20px",
      }}
    >
      {/* Left button - Previous Bar */}
      <CircularButton
        onClick={handlePreviousBar}
        disabled={currentBar === 0 || (!loop && currentBar <= 1)}
        size={44}
        ariaLabel="Previous bar"
        tooltip="Previous bar"
        tooltipPosition="top"
        tooltipShortcut="←"
      >
        {FaChevronLeft({ size: 14 })}
      </CircularButton>

      {/* Center - Bar Sphere */}
      <div
        className="bar-sphere"
        style={{
          borderRadius: "50%",
          background: "var(--overlay-bg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          WebkitBackdropFilter: "blur(7px)",
          backdropFilter: "blur(7px)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          transition: "all 0.15s ease-out",
          width: 150,
          height: 150,
        }}
      >
        {/* Reset button inside sphere - positioned at top */}
        {onReset && (
          <div
            className="bar-sphere-reset-wrapper"
            style={{
              position: "absolute",
              top: "13%",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <CircularButton
              onClick={() => onReset()}
              ariaLabel="Reset"
              size={34}
              tooltip="Reset"
              tooltipShortcut="R"
              tooltipPosition="top"
            >
              {RiResetLeftLine({ size: 18 })}
            </CircularButton>
          </div>
        )}

        <div
          className="bar-sphere-text"
          style={{
            fontWeight: 600,
            color: "var(--text-primary)",
            textAlign: "center",
            userSelect: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            position: "relative",
          }}
        >
          {/* Label */}
          <div
            style={{
              color: "var(--text-secondary)",
              userSelect: "none",
              position: "absolute",
              top: "64%",
              left: "50%",
              transform: "translateX(-50%)",
              whiteSpace: "nowrap",
              fontSize: 12,
              letterSpacing: 0.2,
            }}
          >
            {currentBar === 0 ? "Count-in" : "Bar"}
          </div>

          {/* Current/Total on one line (centered). Hidden during count-in (currentBar === 0). */}
          {currentBar !== 0 && (
            <div
              className="bar-sphere-numbers"
              style={{
                fontWeight: 600,
                color: "var(--text-primary)",
                lineHeight: 1,
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: 30,
                letterSpacing: 0.3,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {/* Left (editable) side: current/start bar */}
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {isEditingStartBar ? (
                  <input
                    type="text"
                    inputMode="numeric"
                    value={startBarInputValue}
                    onChange={handleStartBarInputChange}
                    onBlur={handleStartBarInputBlur}
                    onFocus={handleStartBarInputFocus}
                    onKeyDown={handleStartBarInputKeyDown}
                    autoFocus
                    autoComplete="off"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--text-primary)",
                      fontWeight: 600,
                      fontSize: 30,
                      textAlign: "right",
                      outline: "none",
                      caretColor: "var(--text-primary)",
                      fontFamily: "inherit",
                      width: 40,
                    }}
                    className="bar-sphere-input"
                    aria-label="Set starting bar"
                  />
                ) : (
                  <span
                    onClick={handleStartBarClick}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.2s ease-out",
                      display: "inline-block",
                    }}
                    className="bar-sphere-start-bar"
                    aria-label="Current bar"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "var(--button-hover-bg)";
                      e.currentTarget.style.borderRadius = "10px";
                      e.currentTarget.style.padding = "2px 6px";
                      e.currentTarget.style.margin = "-2px -6px";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.borderRadius = "0";
                      e.currentTarget.style.padding = "0";
                      e.currentTarget.style.margin = "0";
                    }}
                  >
                    {currentBar}
                  </span>
                )}
              </span>
              <span>/</span>
              <span>{totalBars}</span>
            </div>
          )}
        </div>
      </div>

      {/* Right button - Next Bar */}
      <CircularButton
        onClick={handleNextBar}
        disabled={currentBar === 0 || (!loop && currentBar >= totalBars)}
        size={44}
        ariaLabel="Next bar"
        tooltip="Next bar"
        tooltipShortcut="→"
        tooltipPosition="top"
      >
        {FaChevronRight({ size: 14 })}
      </CircularButton>
    </div>
  );
};
